import { useRef } from "react";
import { useSelector } from "react-redux";
import ReactDOM from "react-dom";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { ORDER_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { LinesOrdersMaps } from "../../reducers/linesOrders/mappers";
import { DraggableConsumer } from "../../draggable/DraggableContext";

import type { ReduxState } from "../../reducers/reducers";
import type { DraggableProviderContext } from "../../draggable/DraggableContext";

export type CollectiveOrderBadgeProps = {
    x: number;
    y: number;
    width: number;
    height: number;
    order_uuid: string;
    is_unscheduled: boolean;
};

const CollectiveOrderBadge = (props: CollectiveOrderBadgeProps) => {
    const text_ref: { current: SVGTextElement | null } = useRef(null);
    const { is_collective_order, is_leading_collective_order, multiselect_order_uuids } = useSelector(
        (state: ReduxState) => {
            const multiselect_order_uuids = state.gantt_chart_planning_table.multiselect_order_uuids;
            const planning_table = state.gantt_chart_planning_table;
            let is_collective_order = false;
            let is_leading_collective_order = false;
            if (planning_table.last_clicked_order_uuid !== null) {
                const reschedule_order =
                    LinesOrdersMaps.all_orders_map.get(planning_table.last_clicked_order_uuid) ?? null;
                const order = LinesOrdersMaps.all_orders_map.get(props.order_uuid) ?? null;
                if (reschedule_order !== null && order !== null) {
                    const reschedule_leading_order_id = ORDER_TAGS_ACCESS.leading_order(reschedule_order.tags);
                    const leading_order_id = ORDER_TAGS_ACCESS.leading_order(order.tags);

                    is_collective_order = leading_order_id !== "" && leading_order_id === reschedule_leading_order_id;
                    is_leading_collective_order = is_collective_order && leading_order_id === order.external_id;
                }
            }

            return {
                is_collective_order,
                is_leading_collective_order,
                multiselect_order_uuids,
            };
        }
    );
    if (!is_collective_order && !is_leading_collective_order) {
        return null;
    }

    const order_uuid = props.order_uuid;
    const x = props.x + props.width;
    const y = props.y + props.height / 2 + (props.is_unscheduled ? 6 : 0);
    const text_width = text_ref.current !== null ? text_ref.current.getComputedTextLength() : 30;
    const font_size = 11;
    const padding_x = 4;
    const width = text_width + 2 * padding_x;
    const height = props.height / 2;
    const padding_y = (height - font_size) / 2;
    const label = is_leading_collective_order ? "CL" : "CM";
    const fill = is_leading_collective_order ? "#2186eb" : "#7b8794";
    const title = is_leading_collective_order
        ? translate("common.leading_order_of_collective_order_group", "Leading order of collective order group")
        : translate("common.member_of_collective_order_group", "Member of collective order group");
    const order_badges_group = document.getElementById("gantt-chart-order-badges");
    return (
        <DraggableConsumer>
            {(value?: DraggableProviderContext | null) => {
                const Badge = (
                    <g>
                        <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={fill}
                            stroke="black"
                            strokeWidth="1"
                            rx={4}
                        >
                            <title>{title}</title>
                        </rect>
                        <text
                            ref={text_ref}
                            fontSize={font_size}
                            fontWeight="500"
                            x={x + padding_x}
                            y={y + padding_y + 1}
                            fill="#fff"
                            textAnchor="start"
                            alignmentBaseline="hanging"
                            // @ts-ignore
                            order_uuid={order_uuid}
                        >
                            {label}
                        </text>
                    </g>
                );

                const dragging_order_uuid = value?.state?.dragging_order?.order_uuid ?? "";
                const order_uuids = [order_uuid, ...(multiselect_order_uuids ?? [])];
                if (props.is_unscheduled || order_badges_group === null || order_uuids.includes(dragging_order_uuid)) {
                    return Badge;
                }

                return ReactDOM.createPortal(Badge, order_badges_group);
            }}
        </DraggableConsumer>
    );
};

export default CollectiveOrderBadge;
